/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../../config/enum';
import DefaultApiParameters from '../../../enum/DefaultApiParameters';
import { ApiRequest } from '../../../interface/common.interface';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
} from '../../../interface/filter.interface';
import {
  ClearFilters,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../../shared-functions/CommonFilters';
import ocpiApi from '../../../utils/api/ocpi';

/**
 * Dispatches a notification message using the shared notification slice's action type.
 * Avoids a circular import on the notification slice from within this one.
 * @param {any} dispatch - The thunk dispatch function.
 * @param {any} message - The message to show.
 * @param {any} type - The notification type.
 */
const notify = (dispatch: any, message: string, type: 'success' | 'error') => {
  dispatch({
    type: 'notification/setNotificationMessage',
    payload: { status: true, message, type },
  });
};

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

interface CpoSessionsRequest extends ApiRequest {
  countryCode: string;
  partyId: string;
  counterPartyId?: string;
  counterCountryCode?: string;
}

interface FetchCpoSessionsResponse {
  results: any[];
  limit: number;
  page: number;
  totalCount: number;
}

/**
 * Fetches a page of OCPI charging sessions for a CPO role from the internal gateway.
 */
export const fetchCpoSessions = createAsyncThunk<FetchCpoSessionsResponse, CpoSessionsRequest>(
  'cpo-sessions/fetchCpoSessions',
  async ({ countryCode, partyId, counterPartyId, counterCountryCode, page, limit }, { dispatch, rejectWithValue }) => {
    try {
      const resolvedPage = page || DefaultApiParameters.page;
      const resolvedLimit = limit || DefaultApiParameters.limit;
      const response = await ocpiApi.getInternalCpoSessions(
        countryCode,
        partyId,
        counterPartyId ?? '',
        counterCountryCode ?? '',
        resolvedPage,
        resolvedLimit,
      );
      const results = response?.data?.data ?? response?.data ?? [];
      const normalizedResults = Array.isArray(results) ? results : [];
      return {
        results: normalizedResults,
        limit: resolvedLimit,
        page: resolvedPage,
        totalCount: response?.data?.pagination?.totalCount ?? normalizedResults.length,
      };
    } catch (error: any) {
      notify(
        dispatch,
        error?.response?.data?.message || 'Failed to fetch OCPI sessions.',
        'error',
      );
      return rejectWithValue(error?.response?.data);
    }
  },
);

/**
 * Resolves the filter list for the CPO sessions table. Filter options are known upfront (there
 * is no backend filter-list endpoint for OCPI sessions), so this only exists to satisfy
 * CustomTable's filterListToDispatch contract.
 */
export const fetchCpoSessionFilters = createAsyncThunk<CommonFilterProperties[], void>(
  'cpo-sessions/fetchCpoSessionFilters',
  async () => availableFilters,
);

interface CPOSessionsInitialState {
  sessions: any[];
  limit: number;
  page: number;
  totalPages: number;
  totalResults: number;
  loading: boolean;
  filters: CommonFilterProperties[];
  filterListLoading: boolean;
  isFilterUpdated: boolean;
  sortBy: 'asc' | 'desc' | null;
  sortField: string | null;
}

const initialState: CPOSessionsInitialState = {
  sessions: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: null,
  sortField: null,
};

export const cpoSessionsSlice = createSlice({
  name: 'cpo-sessions',
  initialState,
  reducers: {
    /**
     * Update CPO Sessions filters
     * @param {any} state - The current slice state.
     * @param {any} action - The action payload containing filter updates.
     */
    updateCPOSessionsFilters: (state: any, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
        state.sortBy = null;
        state.sortField = null;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state - The current slice state.
     */
    disableCpoSessionsFilterApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state - The current slice state.
     * @param {any} action - The action payload containing sorting criteria.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Extra reducers to handle thunk requests.
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchCpoSessions.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCpoSessions.fulfilled, (state, action) => {
        state.loading = false;
        state.sessions = action.payload.results;
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalResults = action.payload.totalCount;
        state.totalPages = Math.max(1, Math.ceil(action.payload.totalCount / action.payload.limit));
      })
      .addCase(fetchCpoSessions.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchCpoSessionFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(fetchCpoSessionFilters.fulfilled, (state, action) => {
        state.filterListLoading = false;
        state.filters = action.payload;
      })
      .addCase(fetchCpoSessionFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

export const { updateCPOSessionsFilters, disableCpoSessionsFilterApplied, setSortData } =
  cpoSessionsSlice.actions;

export default cpoSessionsSlice.reducer;
